import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { theme } from '@/lib/theme';
import type { TopCustomer } from '@/hooks/useDashboardData';

interface Props {
  customers: TopCustomer[];
  loading: boolean;
}

const medals = ['🥇', '🥈', '🥉'];

export default function TopCustomersWidget({ customers, loading }: Props) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: 'easeOut', delay: 0.05 }}
      className="rounded-xl p-5 border flex flex-col"
      style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white text-sm font-semibold" style={{ fontFamily: theme.fonts.display }}>
          Top clientes
        </h4>
        <div className="w-6 h-6 flex items-center justify-center">
          <Trophy size={14} color={theme.colors.primary} />
        </div>
      </div>

      {loading && (
        <div className="space-y-3 animate-pulse flex-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-5 h-4 bg-white/8 rounded flex-shrink-0" />
              <div className="flex-1 h-8 bg-white/8 rounded-lg" />
              <div className="w-16 h-4 bg-white/8 rounded" />
            </div>
          ))}
        </div>
      )}

      {!loading && customers.length === 0 && (
        <div className="flex-1 flex items-center justify-center py-4">
          <p className="text-sm" style={{ color: theme.colors.textDisabled }}>
            Sin datos aún
          </p>
        </div>
      )}

      {!loading && customers.length > 0 && (
        <div className="space-y-2 flex-1">
          {customers.map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 + 0.15 }}
              onClick={() => navigate('/admin/clientes')}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer text-left transition-colors"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.07)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.03)';
              }}
            >
              <span className="text-base w-5 flex-shrink-0 text-center leading-none">
                {medals[i] ?? <span className="text-xs font-mono" style={{ color: theme.colors.textMuted }}>{i + 1}</span>}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white/85 text-xs font-medium truncate">{c.name}</p>
                <p className="text-xs truncate" style={{ color: theme.colors.textDisabled }}>
                  {c.total_appointments} {c.total_appointments === 1 ? 'cita' : 'citas'}
                </p>
              </div>
              <span
                className="text-xs font-semibold whitespace-nowrap flex-shrink-0"
                style={{ color: theme.colors.primary }}
              >
                ${c.total_spent.toLocaleString('es-CO')}
              </span>
            </motion.button>
          ))}
        </div>
      )}

      <button
        onClick={() => navigate('/admin/clientes')}
        className="mt-4 text-xs cursor-pointer transition-colors whitespace-nowrap"
        style={{ color: theme.colors.textMuted }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = theme.colors.primary; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = theme.colors.textMuted; }}
      >
        Ver todos los clientes →
      </button>
    </motion.div>
  );
}
